import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'

/**
 * Custom useQuery wrapper that provides better defaults for authenticated requests
 *
 * This hook:
 * - Automatically retries on 401 errors (token will be refreshed by global handler)
 * - Provides consistent error handling
 * - Uses sensible defaults for authenticated requests
 */
export function useAuthQuery<
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  return useQuery({
    // Default retry behavior for authenticated queries
    retry: (failureCount, error) => {
      // Don't retry more than twice
      if (failureCount > 2) return false

      // Retry on 401 (will trigger token refresh)
      if (error instanceof AxiosError && error.response?.status === 401) {
        return failureCount <= 1
      }

      // Retry on network errors
      if (error instanceof AxiosError && !error.response) {
        return failureCount <= 1
      }

      return false
    },
    ...options,
  })
}
